import { inject, Injectable, signal } from '@angular/core';
import {
  AirportSearchErrorService,
  CheckInStaticAem,
  LoggingService,
  OauthAEMConfig,
  RetrieveSearchService,
  UserDataService
} from '@com-delta-idp/mnx-sitewide-shared-core-smart';
import { take } from 'rxjs/operators';

import { FormGroup } from '@angular/forms';
import {
  CheckInWidget,
  CheckInWidgetFormType,
  CityCodeResult,
  GenericAction
} from '@com-delta-idp/mnx-capability-check-in-ui';
import {
  AirportDropDown,
  AirportDropDownList,
  CityData
} from '@com-delta-idp/mnx-sitewide-shared-core-ui';
import { CheckInAdapterService } from '../adapter/check-in-adapter.service';
import { CHECK_IN_SMART_CONSTANTS } from '../models/check-in-status.constants';
import { DataService } from '../services';
import { CheckInFormService } from '../utils/check-in-form.service';
@Injectable({
  providedIn: 'root'
})
export class CheckInFacade {
  predictiveCities: GenericAction[] = [];
  airportSearchErrorService = inject(AirportSearchErrorService);
  private readonly retrieveSearchService = inject(RetrieveSearchService);
  private readonly dataService = inject(DataService);
  adapter = inject(CheckInAdapterService);
  loggingService = inject(LoggingService);
  protected userData = inject(UserDataService);
  formService = inject(CheckInFormService);

  checkInStaticContent = signal<CheckInWidget>({} as CheckInWidget);
  airportModalContent = signal<AirportDropDown>({} as AirportDropDown);
  showAlert = signal<boolean>(false);

  checkInForm!: FormGroup<CheckInWidgetFormType>;

  init(content?: CheckInStaticAem): void {
    this.userData
      .getAuthConfigContent()
      .subscribe((authConfig: OauthAEMConfig) => {
        if (content && content.checkInForm) {
          this.checkInStaticContent.set(
            this.adapter.getContent(content, authConfig.NAME_LENGTH_CONFIG)
          );
          this.airportModalContent.set(
            this.adapter.initializeAirportModalContent()
          );
          const errorMsg =
            content?.checkInForm?.error?.predictiveCitiesNoResults || '';
          this.airportSearchErrorService.setErrorMessageFromAEM(errorMsg);
        } else {
          this.dataService
            .getCheckInStaticContent$()
            .pipe(take(1))
            .subscribe(
              (data) => {
                if (data) {
                  this.checkInStaticContent.set(
                    this.adapter.getContent(data, authConfig.NAME_LENGTH_CONFIG)
                  );
                  this.airportModalContent.set(
                    this.adapter.initializeAirportModalContent()
                  );
                  const errorMsg =
                    data?.checkInForm?.error?.predictiveCitiesNoResults ||
                    'No airports found. Please check your spelling or try a different search.';
                  this.airportSearchErrorService.setErrorMessageFromAEM(
                    errorMsg
                  );
                } else {
                  this.showAlert.set(true);
                }
              },
              (error) => {
                this.loggingService.log(
                  JSON.stringify({
                    message:
                      CHECK_IN_SMART_CONSTANTS.ERROR_MESSAGES
                        .AEM_CONTENT_UNAVAILABLE_ERROR,
                    error: error
                  })
                );
                this.showAlert.set(true);
              }
            );
        }
        this.checkInForm = this.formService.createCheckInForm();
      });
  }

  searchForAirport(searchTerm: string): void {
    this.retrieveSearchService
      .getPredictiveCities(`/${searchTerm}`)
      .pipe(take(1))
      .subscribe({
        next: (response: CityCodeResult[]) => {
          if (response.length > 0) {
            this.airportModalContent.set(
              this.adapter.initializeAirportModalContent(response)
            );
          }
          this.airportSearchErrorService.handleEmptyResults(
            response.length,
            searchTerm
          );
        },
        error: (error) => {
          this.loggingService.log(
            JSON.stringify({
              message:
                CHECK_IN_SMART_CONSTANTS.ERROR_MESSAGES.PREDICTIVE_CITIES_ERROR,
              error: error
            })
          );
          this.predictiveCities = [];
          this.airportSearchErrorService.handleApiError(searchTerm);
        }
      });
  }

  convertCityDataToAirportDropDown(city: CityData): AirportDropDownList {
    return {
      code: this.getCodeValue(city),
      name: this.getNameValue(city)
    };
  }

  private getCodeValue(city: CityData): string {
    return city.id || city.code || '';
  }

  private getNameValue(city: CityData): string {
    return city.label || city.name || '';
  }
}
